/*
 * Copyright (c) 2020. Bolmate.nl
 */

export type FieldDefinition = {
    expectedType: ModelFieldType; convertToBoolean: boolean; allowNull?: boolean; allowUndefined?: boolean;
    defaultValue?: any
};

export enum ModelFieldType {STRING, NUMBER, BOOLEAN, OBJECT, ARRAY}

export const createFieldDef = (expectedType: ModelFieldType, defaultValue?: any, allowNull: boolean = false,
                               allowUndefined: boolean = false,
                               convertToBoolean: boolean = false): FieldDefinition => ({
    expectedType,
    allowUndefined,
    allowNull,
    convertToBoolean,
    defaultValue
});

export abstract class BaseModel<Props extends Record<string, any>, Fields extends Record<string, any>> extends Object {
    private readonly fieldFilter: string[];
    protected readonly fields: Fields & Record<string, any>;
    protected readonly props: Props;
    private readonly FieldsDefinition: Record<string, FieldDefinition>;
    private readonly tempFields: Record<string, any>;
    protected abstract build: (data?: Props) => void;

    protected constructor(fieldFilter: string[], fieldsDefinition: Record<string, FieldDefinition>, fields: Fields,
                          props: Props | undefined) {
        super(props);
        fields = {...fields};
        this.fields = fields;
        this.props = props || {} as Props;
        this.fieldFilter = fieldFilter;
        this.FieldsDefinition = fieldsDefinition;
        this.tempFields = {};
    }

    setField = (key: string, value: any) => {
        if (this.fields.hasOwnProperty(key))
            (this.fields as Record<string, any>)[key] = value;
        return this;
    };

    protected constructFields = (data = this.props) => {
        const preparedData = this.prepareData(data);
        const keys = Object.keys(this.fields).filter(key => !this.fieldFilter.includes(key));
        for (const key of keys) this.initializeField(key, preparedData[key]);
    };

    private initializeField = (key: string, value: any) => {
        if (this.fields.hasOwnProperty(key) && value !== undefined)
            (this.fields as Record<string, any>)[key] = value;
    };

    private prepareData = (data: Props) => {
        const prepareKeys = Object.keys(this.FieldsDefinition), preparedData: Record<string, any> = {};
        for (const key of prepareKeys) {
            const definition: FieldDefinition = this.FieldsDefinition[key],
                {allowNull, allowUndefined, defaultValue} = definition;
            let value = data[key];
            if ((value === null && !allowNull) || (value === undefined && !allowUndefined)) {
                if (defaultValue) preparedData[key] = defaultValue;
                else preparedData[key] = preparedData[key] = this.determineDefaultValue(definition);
            } else if ((value === null && allowNull) || (value === undefined && (allowUndefined || allowNull))) {
                preparedData[key] = null;
            } else
                preparedData[key] = this.validateExistingValue(value, definition);
        }
        return preparedData;
    };

    private determineDefaultValue = (definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition,
            typeToUse = convertToBoolean ? 'boolean' : expectedType;
        switch (typeToUse) {
            case ModelFieldType.STRING:
                return '';
            case ModelFieldType.NUMBER:
                return 0;
            case ModelFieldType.BOOLEAN:
                return false;
            case ModelFieldType.OBJECT:
                return {};
            case ModelFieldType.ARRAY:
                return new Array<any>();
        }
        return null;
    };

    private validateExistingValue = (value: any, definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition;
        if (convertToBoolean && (value === true || value === false)) return value;
        switch (expectedType) {
            case ModelFieldType.STRING:
                if (typeof value !== 'string') value = value.toString();
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.NUMBER:
                if (typeof value !== 'number') value = Number(value);
                if (Number.isNaN(value)) value = 0;
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.BOOLEAN:
                if (value !== true && value !== false) value = false;
                return value;
            case ModelFieldType.OBJECT:
                try {
                    Object.keys(value).length;
                } catch {
                    value = {};
                }
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.ARRAY:
                if (!Array.isArray(value)) value = [];
                return this.convertBoolean(value, expectedType, convertToBoolean);
            default:
                return null;
        }
    };

    private convertBoolean = (value: any, expectedType: ModelFieldType, shouldConvert: boolean = false): any => {
        if (!shouldConvert) return value;
        switch (expectedType) {
            case ModelFieldType.STRING:
                return value === 'true';
            case ModelFieldType.NUMBER:
                return value === 1;
            case ModelFieldType.BOOLEAN:
                return value;
            case ModelFieldType.OBJECT:
                try {
                    return Object.keys(value).length > 0;
                } catch {
                    return false;
                }
            case ModelFieldType.ARRAY:
                try {
                    return value.length > 0;
                } catch {
                    return false;
                }
        }
        return false;
    };
}